import type {
    ChartAccount,
    OperationLineDraft,
    OperationTemplateDetail,
} from '@/api/models';
import type { DraftPair, PendingDrop } from './operationEditorMeta';
import { isSlot } from './operationEditorMeta';

/**
 * What one account box says when it is closed.
 *
 * The box is the field: clicking it opens the same box into its editing state
 * rather than a drawer, so this is the *read* of a line and the inline editor is
 * the write. Keeping the read pure means the closed box can be tested without
 * rendering anything, which matters because it is the surface an author scans
 * to check a recipe before publishing it.
 */

/** How many candidate codes a slot lists before it gives up and says "…". */
const MAX_CANDIDATES = 3;

/**
 * The codes a slot could resolve to — `1110 / 1210 / 1410`.
 *
 * Derived from the filter prefixes against the chart the editor already holds,
 * because `accountFilter` stores prefixes (`["11", "12"]`) and prefixes are not
 * what an accountant reads. Truncated at three: the point is to show the shape
 * of the answer, and a slot narrowed to forty accounts is better described by
 * its label than by forty codes.
 *
 * `null` for an unfiltered slot — every postable account is a candidate, and
 * listing three of two hundred would imply a narrowing that is not there.
 */
export const slotCandidateCodes = (
    accountFilter: readonly string[],
    accounts: readonly ChartAccount[],
): string | null => {
    if (accountFilter.length === 0) {
        return null;
    }
    const matches = accounts
        .filter((account) =>
            accountFilter.some((prefix) => account.code.startsWith(prefix)),
        )
        .map((account) => account.code);

    if (matches.length === 0) {
        // The filter matches nothing in this chart. Say the prefixes rather
        // than nothing at all: a filter of "99" that resolves to no account is
        // a mistake the author needs to see, not an empty box.
        return accountFilter.join(' / ');
    }
    const shown = matches.slice(0, MAX_CANDIDATES).join(' / ');
    return matches.length > MAX_CANDIDATES ? `${shown} …` : shown;
};

/** A translatable sentence: a key and whatever it interpolates. */
export type BoxNote = {
    key: string;
    params: Record<string, string>;
};

/** The closed box, fully resolved. */
export type LineBoxCopy = {
    isSlot: boolean;
    /** The account code, or a slot's candidate codes. `null` when unset. */
    code: string | null;
    /** The account's name, or the slot's Georgian label. */
    name: string | null;
    /** The muted third line: a slot's English label, or the line's role label. */
    detail: string | null;
    /** The footer sentence beside the Fixed/Slot control. */
    note: BoxNote;
};

const nameOf = (
    accountId: string,
    accounts: readonly ChartAccount[],
): ChartAccount | undefined =>
    accounts.find((account) => account.id === accountId);

export const lineBoxCopy = (
    line: OperationLineDraft,
    accounts: readonly ChartAccount[],
): LineBoxCopy => {
    if (isSlot(line)) {
        const label = line.slotLabelEn || line.slotLabelKa || null;
        return {
            isSlot: true,
            code: slotCandidateCodes(line.accountFilter, accounts),
            name: line.slotLabelKa || null,
            detail: line.slotLabelEn || null,
            note: label
                ? { key: 'operations.editor.box.slotNamed', params: { label } }
                : { key: 'operations.editor.box.slotUnnamed', params: {} },
        };
    }

    const account = line.accountId
        ? nameOf(line.accountId, accounts)
        : undefined;
    if (!account) {
        // Two different faults, told apart because the fixes differ. An unset
        // line is waiting for the author to choose. A line pointing at an id
        // the chart does not hold is pointing at an account that was archived,
        // or at another tenant's — and "choose an account" would be the wrong
        // advice for it. The preview panel draws the same distinction.
        return {
            isSlot: false,
            code: null,
            name: null,
            detail: line.roleLabel || null,
            note: {
                key: line.accountId
                    ? 'operations.editor.box.unknownAccount'
                    : 'operations.editor.box.noAccount',
                params: {},
            },
        };
    }

    return {
        isSlot: false,
        code: account.code,
        name: account.name,
        detail: line.roleLabel || null,
        note: {
            key: 'operations.editor.box.fixed',
            params: { code: account.code },
        },
    };
};

/**
 * The other pair using the same slot label, if there is one.
 *
 * **An observation, not a link.** Two slots configured identically are still two
 * slots: the wire has no shared-slot concept, so at record time the operator is
 * asked for the account twice. Saying "also asked in pair 3" is true; saying
 * "shared with pair 3" would not be, and would hide a duplicate question the
 * author probably did not intend.
 *
 * Matched on the labels rather than on the filter, because the label is what the
 * recorder is asked and two slots asking the same question are the duplicate
 * worth reporting.
 */
export const duplicateSlotPairs = (
    pairs: readonly DraftPair[],
    pairGroup: number,
): number[] => {
    const self = pairs.find((pair) => pair.pairGroup === pairGroup);
    if (!self) {
        return [];
    }
    const labels = [self.debit, self.credit]
        .filter((line) => isSlot(line))
        .map((line) => `${line.slotLabelKa ?? ''}|${line.slotLabelEn ?? ''}`)
        .filter((key) => key !== '|');

    if (labels.length === 0) {
        return [];
    }

    return pairs
        .filter((pair) => pair.pairGroup !== pairGroup)
        .filter((pair) =>
            [pair.debit, pair.credit]
                .filter((line) => isSlot(line))
                .some((line) =>
                    labels.includes(
                        `${line.slotLabelKa ?? ''}|${line.slotLabelEn ?? ''}`,
                    ),
                ),
        )
        .map((pair) => pair.pairGroup);
};

/**
 * Which slot labels this recipe will ask the recorder for, deduplicated.
 *
 * Feeds the "Slots resolved at entry: Warehouse, Receiving account" line above
 * the pairs — the one place the author sees the whole set of questions their
 * template will pose, rather than one pair at a time.
 */
export const slotsResolvedAtEntry = (pairs: readonly DraftPair[]): string[] => [
    ...new Set(
        pairs
            .flatMap((pair) => [pair.debit, pair.credit])
            .filter((line) => isSlot(line))
            .map((line) => line.slotLabelEn || line.slotLabelKa || '')
            .filter((label) => label.length > 0),
    ),
];

/**
 * Whether dropping this pair would change what a published version posts.
 *
 * Drives the difference between "Remove pair 2" and "Drop from v4". A pair that
 * has only ever existed in an unpublished draft can be removed with a plain
 * confirmation, because nothing has posted under it and nothing will. A pair
 * that exists in a published version is a different act: the entries already
 * posted keep it, and the next version stops posting it — which is what the
 * dialog has to say.
 *
 * Keyed on the pair's **position**, which is the only handle available: the
 * draft's lines carry no id back to the published version's lines, and
 * `pairGroup` is renumbered dense on every save.
 */
export const isPairPublished = (
    publishedPairCount: number,
    pairIndex: number,
): boolean => pairIndex < publishedPairCount;

/**
 * One line, as the drop dialog names it: `1110` or a slot's label.
 *
 * Deliberately terser than {@link lineBoxCopy} — the dialog is a sentence, and a
 * three-line account block inside it would compete with the consequence it is
 * there to state.
 */
const dropSummaryLine = (
    line: OperationLineDraft,
    accounts: readonly ChartAccount[],
): string => {
    const copy = lineBoxCopy(line, accounts);
    return copy.code ?? copy.name ?? copy.detail ?? '—';
};

/** Freeze what the drop dialog needs before the draft renumbers underneath it. */
export const toPendingDrop = (
    pairs: readonly DraftPair[],
    pairGroup: number,
    accounts: readonly ChartAccount[],
    template: OperationTemplateDetail | null,
): PendingDrop | null => {
    const index = pairs.findIndex((pair) => pair.pairGroup === pairGroup);
    const pair = pairs[index];
    if (!pair) {
        return null;
    }

    const activeVersion = template?.activeVersion;
    const publishedPairCount = activeVersion
        ? new Set(activeVersion.lines.map((line) => line.pairGroup)).size
        : 0;

    const credit = lineBoxCopy(pair.credit, accounts);
    const summary = [
        dropSummaryLine(pair.debit, accounts),
        '→',
        dropSummaryLine(pair.credit, accounts),
        credit.name ? `· ${credit.name}` : null,
    ]
        .filter((part): part is string => part !== null)
        .join(' ');

    return {
        pairGroup,
        displayNumber: index + 1,
        role: pair.role,
        summary,
        isPublished: isPairPublished(publishedPairCount, index),
        activeVersion: activeVersion?.version ?? 0,
    };
};
